/**
 * hali: Bundle-Darstellung auf der PDP (Shopify Bundles).
 *
 * Liquid kennt die Komponenten einer Bundle-Variante nicht (item_components gibt es nur
 * im Warenkorb). Wir laden sie einmalig pro Produkt über die tokenlose Storefront API
 * (nur Produkt-Scope, kein Secret im Browser) und arbeiten danach aus dem Cache –
 * beim Variantenwechsel entsteht kein weiterer Request.
 *
 * Drei Aufgaben:
 * 1. Komponenten-Karten im Variantenpicker (von Liquid gerendert, siehe
 *    snippets/variant-main-picker.liquid) mit Bild und fixen Eigenschaften füllen.
 * 2. Komponenten ohne wählbare Option als eigene Karten im Block anhängen.
 *    Ersparnis: Die Summe der Einzelpreise wird als Streichpreis in den vorhandenen
 *    Preis-Container des Headers gesetzt (snippets/price.liquid) – damit greifen die
 *    Shop-Styles für Aktionspreise (roter Preis, durchgestrichene Summe, MwSt.-Zeile).
 * 3. Swatches: Shopify-Bundles können keine verknüpften Metafeld-Optionen haben, die
 *    Komponentenprodukte aber schon. Wir übernehmen deren Swatches in den Picker.
 * 4. Lieferinfo + Add-to-Cart-Properties: Die Bundle-Variante trägt keine PIM-Metafelder
 *    (lieferart, lieferzeit, montagepreis). Wir laden sie je Komponentenprodukt über
 *    sections/hali-produkt-daten.liquid (Section Rendering API – Liquid hat Metafeld-
 *    Zugriff, die tokenlose Storefront API nicht), füllen den Lieferinfo-Block
 *    (blocks/hali-pdp-lieferinfo.liquid) und setzen _lieferart/_liefertermin/
 *    _montage_preis/_komponenten im Kaufformular – wie bei Einzelprodukten. Der
 *    Warenkorb rechnet dasselbe aus item_components (snippets/hali-bundle-item-data.liquid).
 * 5. Accordions „Technische Daten", „Dokumente & Downloads" und „Lieferinformationen"
 *    (snippets/pdp-accordion-group.liquid): je Komponente ein Abschnitt mit Variantentext
 *    (ofmlvartext) + Maßzeichnung, Downloads bzw. Lieferart, aus denselben Produktdaten wie 4. Reihenfolge wie im Picker: zuerst die
 *    wählbaren Komponenten in Optionsreihenfolge, dann die fixen.
 *
 * Der Picker wird bei jedem Variantenwechsel neu gemorpht, deshalb laufen 1. und 3. im
 * variant:update-Handler erneut. Rendering ausschließlich über createElement/textContent.
 */

const API_VERSION = '2025-10';

const COMPONENT_FIELDS = `
  variants(first: 100) {
    nodes {
      id
      price { amount currencyCode }
      components(first: 30) {
        nodes {
          quantity
          productVariant {
            id
            title
            price { amount currencyCode }
            image { url(transform: { maxWidth: 200 }) altText }
            product {
              title
              handle
              featuredImage { url(transform: { maxWidth: 200 }) altText }
              options {
                name
                optionValues {
                  name
                  swatch {
                    color
                    image { previewImage { url(transform: { maxWidth: 80 }) } }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @typedef {{ productTitle: string, optionName: string, values: Map<string, string> }} SwatchOption
 * @typedef {{ amount: number, currencyCode: string }} Money
 * @typedef {{ components: Map<string, Array>, prices: Map<string, Money>, swatchOptions: SwatchOption[] }} BundleData
 */

/** @type {Map<string, Promise<BundleData>>} */
const cache = new Map();

/**
 * Produktdaten je Komponentenprodukt (Lieferart, Lieferzeit, Montagepreis je Variante,
 * fertige Lieferzeiträume je Lieferzeit-Code). Quelle: sections/hali-produkt-daten.liquid.
 * @typedef {{ src: string, srcset: string, width: number, height: number }} TechnikBild
 * @typedef {{ handle: string, title: string, fachpersonal: boolean, termine: Record<string, string>,
 *   technik_bild: TechnikBild|null, downloads: { url: string, label: string }[],
 *   variants: Record<string, { title: string, lieferart: string, lieferzeit: string, montagepreis: string, technik: string }> }} ProduktDaten
 * @type {Map<string, Promise<ProduktDaten>>}
 */
const productDataCache = new Map();

function loadProductData(rootUrl, handle) {
  if (productDataCache.has(handle)) return productDataCache.get(handle);
  const root = (rootUrl || '/').replace(/\/$/, '');
  const promise = fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=hali-produkt-daten`)
    .then((response) => {
      if (!response.ok) throw new Error(`Produktdaten "${handle}": HTTP ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const json = doc.querySelector('script[data-hali-produkt-daten]')?.textContent;
      if (!json) throw new Error(`Produktdaten "${handle}": Section liefert keine Daten`);
      return JSON.parse(json);
    });
  promise.catch(() => productDataCache.delete(handle));
  productDataCache.set(handle, promise);
  return promise;
}

/**
 * Bei gemischten Lieferarten gewinnt die „längere“ (Vorgabe hali): BML (mit Montage) vor
 * BML (ohne Montage) vor POST. Muss mit snippets/hali-bundle-item-data.liquid übereinstimmen.
 */
const LIEFERART_RANG = { 'bml (mit montage)': 3, 'bml (ohne montage)': 2, post: 1 };

/** Badge-Texte wie blocks/hali-pdp-lieferinfo.liquid */
const LIEFERART_TEXT = {
  post: 'geliefert in Einzelteilen im Karton',
  'bml (mit montage)': 'Montage vor Ort',
  'bml (ohne montage)': 'Lieferung größtenteils zusammengebaut',
};

const TERMIN_MONATE = ['Jan.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

/** Vergleichsmetrik wie cart-summary.liquid / hali-liefertermin-sync.js: Monat*31 + Tag des Endtermins. */
function terminScore(termin) {
  const parts = String(termin || '').split('–');
  if (parts.length !== 2) return -1;
  const tokens = parts[1].trim().split(/\s+/);
  const day = parseInt(tokens[0], 10);
  const month = TERMIN_MONATE.indexOf(tokens[1]);
  if (Number.isNaN(day) || month < 0) return -1;
  return (month + 1) * 31 + day;
}

/** Zahl so formatieren wie Liquid eine Float-Summe ausgibt ("60.0", "62.5"). */
function liquidFloat(n) {
  return Number.isInteger(n) ? `${n}.0` : String(Math.round(n * 100) / 100);
}

function numericId(gid) {
  return String(gid).split('/').pop() ?? '';
}

/** Storefront-Enums sind Großbuchstaben, Liquid liefert "de" bzw. "de-AT" / "AT". */
function enumCode(value) {
  const code = String(value || '').split('-')[0].toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

/** @returns {Money|null} */
function money(price) {
  const amount = Number(price?.amount);
  if (!price?.currencyCode || !Number.isFinite(amount)) return null;
  return { amount, currencyCode: price.currencyCode };
}

/** @param {object|null} swatch @returns {string|null} CSS-Wert für --swatch-background */
function swatchBackground(swatch) {
  const url = swatch?.image?.previewImage?.url;
  if (url) return `url(${JSON.stringify(url)})`;
  const color = swatch?.color;
  if (color && /^#[0-9a-f]{3,8}$/i.test(color)) return color;
  return null;
}

/** @returns {SwatchOption[]} */
function collectSwatchOptions(components) {
  const seen = new Set();
  const result = [];
  for (const list of components.values()) {
    for (const component of list) {
      const product = component.productVariant?.product;
      if (!product?.options || seen.has(product.handle)) continue;
      seen.add(product.handle);
      for (const option of product.options) {
        const values = new Map();
        for (const value of option.optionValues ?? []) {
          const background = swatchBackground(value.swatch);
          if (background) values.set(normalize(value.name), background);
        }
        if (values.size > 0) {
          result.push({ productTitle: product.title, optionName: option.name, values });
        }
      }
    }
  }
  return result;
}

/** @returns {Promise<BundleData>} */
function loadBundleData(handle, context) {
  const key = `${handle}|${context.country}|${context.language}`;
  if (cache.has(key)) return cache.get(key);

  const withContext = context.country && context.language;
  const query = withContext
    ? `query BundleComponents($handle: String!, $country: CountryCode!, $language: LanguageCode!)
        @inContext(country: $country, language: $language) {
        product(handle: $handle) { ${COMPONENT_FIELDS} }
      }`
    : `query BundleComponents($handle: String!) {
        product(handle: $handle) { ${COMPONENT_FIELDS} }
      }`;
  const variables = withContext
    ? { handle, country: context.country, language: context.language }
    : { handle };

  const promise = fetch(`/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Storefront API ${response.status}`);
      return response.json();
    })
    .then((json) => {
      if (json.errors?.length) throw new Error(json.errors[0].message);
      // Entwürfe bzw. nicht im Online-Store-Kanal veröffentlichte Produkte liefert die
      // tokenlose Storefront API als null → Swatches und fixe Komponenten bleiben dann aus.
      if (json.data?.product === null) {
        throw new Error(
          `Produkt "${handle}" ist nicht im Online Store veröffentlicht (Entwurf?) – Storefront API liefert null`
        );
      }
      const nodes = json.data?.product?.variants?.nodes ?? [];
      const components = new Map();
      const prices = new Map();
      for (const variant of nodes) {
        const id = numericId(variant.id);
        components.set(id, variant.components?.nodes ?? []);
        const price = money(variant.price);
        if (price) prices.set(id, price);
      }
      return { components, prices, swatchOptions: collectSwatchOptions(components) };
    });

  promise.catch(() => cache.delete(key));
  cache.set(key, promise);
  return promise;
}

class HaliBundleInhalt extends HTMLElement {
  connectedCallback() {
    this._cards = this.querySelector('.hali-bundle-inhalt__cards');
    this._section = this.closest('.shopify-section, dialog');
    this._data = null;

    // product-price.js tauscht beim Variantenwechsel seinen priceContainer aus. Läuft
    // das nach unserem Handler, setzt der Observer den Streichpreis erneut.
    this._priceObserver = new MutationObserver(() => this.renderHeaderPrice());

    this._handler = (event) => {
      const variantId = event.detail?.resource?.id;
      if (variantId) this.dataset.variantId = String(variantId);
      // Der Picker ist zu diesem Zeitpunkt bereits gemorpht → Karten und Swatches neu füllen.
      this.update();
    };
    this._section?.addEventListener('variant:update', this._handler);

    const handle = this.dataset.productHandle;
    if (!handle || !this._cards) {
      this.hidden = true;
      return;
    }

    loadBundleData(handle, {
      country: enumCode(this.dataset.country),
      language: enumCode(this.dataset.language),
    })
      .then((data) => {
        this._data = data;
        this.update();
        return this.loadProduktDaten(data);
      })
      .catch((error) => {
        console.warn('[hali-bundle-inhalt]', error);
        this.hidden = true;
      });
  }

  /** Produktdaten aller Komponentenprodukte laden; Ausfälle einzelner Produkte nur loggen. */
  loadProduktDaten(data) {
    const handles = new Set();
    for (const list of data.components.values()) {
      for (const component of list) {
        const handle = component.productVariant?.product?.handle;
        if (handle) handles.add(handle);
      }
    }
    return Promise.allSettled(
      [...handles].map((handle) => loadProductData(this.dataset.rootUrl, handle).then((d) => [handle, d]))
    ).then((results) => {
      this._produktDaten = new Map();
      for (const result of results) {
        if (result.status === 'fulfilled') this._produktDaten.set(result.value[0], result.value[1]);
        else console.warn('[hali-bundle-inhalt]', result.reason);
      }
      this.updateLieferinfo();
      this.updateTechnik();
    });
  }

  disconnectedCallback() {
    this._section?.removeEventListener('variant:update', this._handler);
    this._priceObserver?.disconnect();
  }

  get picker() {
    return this._section?.querySelector('variant-picker') ?? null;
  }

  get currencyLocale() {
    return document.documentElement.lang || 'de';
  }

  update() {
    if (!this._data) return;
    const components = this._data.components.get(String(this.dataset.variantId)) ?? [];

    this.decoratePicker();
    const unmatched = this.fillPickerCards(components);
    this.renderFixedCards(unmatched);
    this.renderHeaderPrice();
    this.updateLieferinfo();
    this.updateTechnik();

    this.hidden = unmatched.length === 0;
    this.dataset.state = 'ready';
  }

  /* ── Karten im Picker füllen ──────────────────────────────────────────── */

  /**
   * @param {Array} components Komponenten der gewählten Variante
   * @returns {Array} Komponenten ohne eigene Karte im Picker (= fix im Set)
   */
  fillPickerCards(components) {
    const cards = [...(this.picker?.querySelectorAll('.hali-bundle-card[data-hali-component]') ?? [])];
    const remaining = [...components];
    const matched = [];

    for (const card of cards) {
      const title = normalize(card.dataset.haliComponent);
      const index = remaining.findIndex((c) => normalize(c.productVariant?.product?.title) === title);
      if (index === -1) continue;
      const [component] = remaining.splice(index, 1);
      matched.push(component);

      const chosen = [...card.querySelectorAll('input[type="radio"]:checked, select')].map((el) =>
        normalize(el.value)
      );
      this.fillCardHead(card, component, chosen);
    }
    // Sichtbare Reihenfolge der PDP: Picker-Karten (Optionsreihenfolge), dann fixe Komponenten.
    this._ordered = [...matched, ...remaining];
    return remaining;
  }

  /**
   * @param {HTMLElement} card
   * @param {object} component
   * @param {string[]} chosenValues Werte, die der Kunde in dieser Karte wählt (nicht "fix")
   */
  fillCardHead(card, component, chosenValues) {
    const variant = component.productVariant ?? {};
    const product = variant.product ?? {};

    const thumb = card.querySelector('[data-hali-thumb]');
    if (thumb) this.setThumb(thumb, variant.image ?? product.featuredImage ?? null, product.title);

    const fixed = card.querySelector('[data-hali-fixed]');
    if (fixed) fixed.textContent = this.fixedParts(variant.title, chosenValues).join(' · ');

    const title = card.querySelector('.hali-bundle-card__title');
    this.setQuantity(title, component.quantity);
    this.linkTitle(title, product.handle);
  }

  /** Menge > 1 als Präfix „2 ×“ vor den Titel (idempotent, der Picker wird neu gemorpht). */
  setQuantity(titleEl, quantity) {
    if (!titleEl) return;
    const previous = titleEl.querySelector('.hali-bundle-card__qty');
    if (previous) {
      const space = previous.nextSibling;
      if (space?.nodeType === Node.TEXT_NODE && !space.textContent.trim()) space.remove();
      previous.remove();
    }
    const qty = Number(quantity) || 1;
    if (qty <= 1) return;
    const span = document.createElement('span');
    span.className = 'hali-bundle-card__qty';
    span.textContent = `${qty} ×`;
    titleEl.prepend(span, document.createTextNode(' '));
  }

  /**
   * Variantentitel "Basic White / ohne Kabeldurchführung / 160 x 80 cm" minus der in der
   * Karte wählbaren Werte → ["ohne Kabeldurchführung", "160 x 80 cm"].
   */
  fixedParts(variantTitle, chosenValues) {
    if (!variantTitle || variantTitle === 'Default Title') return [];
    return variantTitle
      .split(' / ')
      .map((part) => part.trim())
      .filter((part) => part && !chosenValues.includes(normalize(part)));
  }

  setThumb(thumb, image, fallbackAlt) {
    let img = thumb.querySelector('img');
    if (!image?.url) {
      img?.remove();
      return;
    }
    if (!img) {
      img = document.createElement('img');
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    if (img.getAttribute('src') !== image.url) img.src = image.url;
    img.alt = image.altText || fallbackAlt || '';
  }

  /** Produkttitel verlinken (Setting), ohne den Mengen-Span anzufassen. */
  linkTitle(titleEl, handle) {
    if (!titleEl || this.dataset.showLinks !== 'true' || !handle) return;
    if (titleEl.querySelector('a.hali-bundle-card__title-link')) return;
    const textNode = [...titleEl.childNodes].find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );
    if (!textNode) return;
    const link = document.createElement('a');
    link.className = 'hali-bundle-card__title-link';
    link.href = this.productUrl(handle);
    link.textContent = textNode.textContent.trim();
    textNode.replaceWith(link);
  }

  productUrl(handle) {
    const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
    return `${root}/products/${encodeURIComponent(handle)}`;
  }

  /* ── Fixe Komponenten als Karten im Block ─────────────────────────────── */

  renderFixedCards(components) {
    if (!this._cards) return;
    this._cards.replaceChildren(...components.map((c) => this.renderFixedCard(c)));
  }

  /** Fixe Komponente: Bild, Titel (mit Menge), fixe Eigenschaften – kein Badge. */
  renderFixedCard(component) {
    const variant = component.productVariant ?? {};
    const product = variant.product ?? {};

    const card = document.createElement('div');
    card.className = 'hali-bundle-card hali-bundle-card--fixed';

    const head = document.createElement('div');
    head.className = 'hali-bundle-card__head';

    const thumb = document.createElement('div');
    thumb.className = 'hali-bundle-card__thumb';
    this.setThumb(thumb, variant.image ?? product.featuredImage ?? null, product.title);
    head.appendChild(thumb);

    const meta = document.createElement('div');
    meta.className = 'hali-bundle-card__meta';

    const title = document.createElement('p');
    title.className = 'hali-bundle-card__title';
    title.textContent = product.title || variant.title || '';
    this.setQuantity(title, component.quantity);
    this.linkTitle(title, product.handle);
    meta.appendChild(title);

    const fixed = document.createElement('p');
    fixed.className = 'hali-bundle-card__fixed';
    fixed.textContent = this.fixedParts(variant.title, []).join(' · ');
    meta.appendChild(fixed);
    head.appendChild(meta);

    card.appendChild(head);
    return card;
  }

  /* ── Ersparnis im Header-Preis ────────────────────────────────────────── */

  /**
   * Summe der Einzelpreise und Ersparnis für die gewählte Variante.
   * @returns {{ single: number, bundle: Money, saving: number }|null}
   */
  savings() {
    if (!this._data || this.dataset.showSavings !== 'true') return null;
    const bundle = this._data.prices.get(String(this.dataset.variantId));
    const components = this._data.components.get(String(this.dataset.variantId)) ?? [];
    if (!bundle || components.length === 0) return null;

    let single = 0;
    for (const component of components) {
      const price = money(component.productVariant?.price);
      if (!price || price.currencyCode !== bundle.currencyCode) return null;
      single += price.amount * Math.max(1, Number(component.quantity) || 1);
    }
    const saving = single - bundle.amount;
    return saving >= 0.005 ? { single, bundle, saving } : null;
  }

  /**
   * Streichpreis (Summe Einzelpreise) + Ersparnis-Zeile in den Preis-Container des
   * Headers setzen. Ein von Liquid gerendeter Streichpreis (echter compare_at_price)
   * hat Vorrang und wird nicht angefasst.
   */
  renderHeaderPrice() {
    const productPrice = this._section?.querySelector('product-price');
    const container = productPrice?.querySelector('[ref="priceContainer"]');
    if (!productPrice || !container) return;

    this._priceObserver.observe(productPrice, { childList: true });

    container.querySelector('[data-hali-bundle-compare]')?.remove();
    container.querySelector('[data-hali-bundle-saving]')?.remove();

    const data = this.savings();
    this.renderSetBadge(data);
    const priceEl = container.querySelector('.price');
    if (!data || !priceEl) return;
    // Echter compare_at_price (z.B. von der Bundle-Pricing-App gesetzt): Liquid rendert den
    // Streichpreis selbst, wir ergänzen dann nur die Ersparnis-Zeile.
    const hasLiquidCompare = Boolean(container.querySelector('.compare-at-price'));

    // Darstellung wie snippets/price.liquid: Währungscode nur, wenn der Preis ihn zeigt;
    // B2B-Kunden sehen Netto (erkennbar an "exkl." in der MwSt.-Zeile).
    const withCurrency = /\s[A-Z]{3}$/.test(priceEl.textContent.trim());
    const mwst = container.querySelector('.hali-pdp-mwst');
    const isNet = /^exkl\./i.test(mwst?.textContent.trim() ?? '');
    const divisor = Number(window.HALI_TAX_DIVISOR) || 120;
    const toDisplay = (amount) => (isNet ? (amount * 100) / divisor : amount);

    if (!hasLiquidCompare) {
      const group = document.createElement('span');
      group.setAttribute('role', 'group');
      group.dataset.haliBundleCompare = '';
      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = 'Einzeln gekauft\u00a0';
      const compare = document.createElement('span');
      compare.className = 'compare-at-price';
      compare.append(...this.moneyNodes(toDisplay(data.single), data.bundle.currencyCode, withCurrency));
      group.append(hiddenLabel, compare);

      // Nach dem Preis (bzw. dessen role="group"-Wrapper) einfügen, vor der MwSt.-Zeile.
      const anchor = priceEl.closest('[role="group"]') ?? priceEl;
      anchor.after(group);
    }

    const percent = Math.round((data.saving / data.single) * 100);
    const line = document.createElement('p');
    line.className = 'hali-pdp-mwst hali-pdp-bundle-saving';
    line.dataset.haliBundleSaving = '';
    line.append(
      document.createTextNode('Du sparst '),
      ...this.moneyNodes(toDisplay(data.saving), data.bundle.currencyCode, false),
      document.createTextNode(percent > 0 ? ` (${percent} %) gegenüber dem Einzelkauf` : ' gegenüber dem Einzelkauf')
    );
    (mwst ?? container.querySelector('.compare-at-price')?.closest('[role="group"]') ?? priceEl).after(line);
  }

  /**
   * „SET-PREIS -x%“-Badge an der Galerie (blocks/_product-media-gallery.liquid): Prozent
   * aus der Ersparnis gegenüber der Summe der Einzelpreise der gewählten Variante. Ohne
   * Daten bleibt der Liquid-Stand (Vergleichspreis) stehen.
   */
  renderSetBadge(data) {
    const badge = this._section?.querySelector('[data-hali-set-badge]');
    if (!badge || !data) return;
    const percent = Math.round((data.saving / data.single) * 100);
    badge.textContent = percent > 0 ? `SET-PREIS -${percent}%` : 'SET-PREIS';
    badge.hidden = false;
  }

  /**
   * Betrag wie snippets/hali-money.liquid: "€875", "€787<span class=hali-price-decimals>,50</span> EUR".
   * @returns {Node[]}
   */
  moneyNodes(amount, currencyCode, withCurrency) {
    const nodes = [];
    try {
      const parts = new Intl.NumberFormat(this.currencyLocale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).formatToParts(amount);
      const symbol = parts.find((part) => part.type === 'currency')?.value ?? currencyCode;
      const integer = parts
        .filter((part) => ['integer', 'group', 'minusSign'].includes(part.type))
        .map((part) => part.value)
        .join('');
      const decimal = parts.find((part) => part.type === 'decimal')?.value ?? ',';
      const fraction = parts.find((part) => part.type === 'fraction')?.value ?? '00';

      nodes.push(document.createTextNode(`${symbol}${integer}`));
      if (fraction !== '00') {
        const decimals = document.createElement('span');
        decimals.className = 'hali-price-decimals';
        decimals.textContent = `${decimal}${fraction}`;
        nodes.push(decimals);
      }
      if (withCurrency) nodes.push(document.createTextNode(` ${currencyCode}`));
    } catch {
      nodes.push(document.createTextNode(`${amount.toFixed(2)} ${currencyCode}`));
    }
    return nodes;
  }

  /* ── Lieferinfo + Formular-Properties aus den Komponenten ─────────────── */

  /**
   * Liefer- und Montagedaten der gewählten Variante – gleiche Regeln wie
   * snippets/hali-bundle-item-data.liquid im Warenkorb (dort aus item_components).
   * @returns {{ montage: number, lieferart: string, liefertermin: string, fachpersonal: boolean, komponenten: object[] }|null}
   */
  lieferdaten() {
    if (!this._data || !this._produktDaten) return null;
    const components = this._data.components.get(String(this.dataset.variantId)) ?? [];
    if (components.length === 0) return null;

    let montage = 0;
    let lieferart = '';
    let lieferartRang = 0;
    let liefertermin = '';
    let terminRang = -1;
    let fachpersonal = false;
    const komponenten = [];

    for (const component of components) {
      const variant = component.productVariant ?? {};
      const product = variant.product ?? {};
      const daten = this._produktDaten.get(product.handle);
      const vd = daten?.variants?.[numericId(variant.id)];
      // Unvollständige Daten → lieber keine Lieferinfo als falsche Properties im Auftrag.
      if (!vd) return null;
      const menge = Math.max(1, Number(component.quantity) || 1);

      montage += (parseFloat(vd.montagepreis) || 0) * menge;

      const rang = LIEFERART_RANG[normalize(vd.lieferart)] ?? 0;
      if (vd.lieferart && rang > lieferartRang) {
        lieferartRang = rang;
        lieferart = vd.lieferart;
      }

      const termin = (vd.lieferzeit && daten.termine?.[vd.lieferzeit]) || '';
      const score = terminScore(termin);
      if (score > terminRang) {
        terminRang = score;
        liefertermin = termin;
      }

      if (daten.fachpersonal) fachpersonal = true;

      // Titel wie line_item.title im Warenkorb: "Produkt - Variante"
      const variantTitle = variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
      komponenten.push({
        variant_id: Number(numericId(variant.id)),
        titel: `${product.title ?? ''}${variantTitle}`,
        menge,
        lieferart: vd.lieferart || '',
        lieferzeit: vd.lieferzeit || '',
        liefertermin: termin,
        montage_preis: String(vd.montagepreis ?? '0'),
      });
    }
    return { montage, lieferart, liefertermin, fachpersonal, komponenten };
  }

  updateLieferinfo() {
    const daten = this.lieferdaten();
    this.renderLieferinfo(daten);
    this.syncFormInputs(daten);
  }

  /** Platzhalter aus blocks/hali-pdp-lieferinfo.liquid (data-hali-bundle) füllen. */
  renderLieferinfo(daten) {
    const el = this._section?.querySelector('hali-lieferinfo[data-hali-bundle]');
    if (!el) return;
    // Lieferart je Komponente steht im Accordion „Lieferinformationen“, hier nur „Lieferung“
    const text = daten?.lieferart ? 'Lieferung' : '';
    const termin = daten?.liefertermin ?? '';
    const badge = el.querySelector('[data-hali-badge]');
    const date = el.querySelector('[data-hali-date]');
    if (badge) badge.textContent = text;
    if (date) date.textContent = termin;
    el.hidden = !text && !termin;
  }

  /**
   * Add-to-Cart-Properties wie bei Einzelprodukten setzen. Liquid rendert für die
   * Bundle-Variante _montage_preis=0 und keine _lieferart/_liefertermin-Inputs
   * (Metafelder fehlen). Der Warenkorb-Sync (hali-liefertermin-sync.js) bleibt als
   * Absicherung und rechnet aus item_components dasselbe.
   */
  syncFormInputs(daten) {
    const form = this._section?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const values = {
      _montage_preis: daten ? liquidFloat(daten.montage) : '0',
      _lieferart: daten?.lieferart ?? '',
      _liefertermin: daten?.liefertermin ?? '',
      _komponenten: daten ? JSON.stringify(daten.komponenten) : '',
    };
    for (const [key, value] of Object.entries(values)) {
      const name = `properties[${key}]`;
      let input =
        form.querySelector(`input[name="${name}"]`) ??
        (form.id ? this._section.querySelector(`input[name="${name}"][form="${form.id}"]`) : null);
      if (!value) {
        if (input && key !== '_montage_preis') input.remove();
        continue;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      if (input.value !== value) input.value = value;
    }
  }

  /* ── Technische Daten + Downloads der Komponenten in den Accordions ───── */

  /**
   * Container in snippets/pdp-accordion-group.liquid füllen. Läuft bei jedem
   * Variantenwechsel (Variantentext ändert sich mit der Komponenten-Variante) und
   * sobald die Produktdaten geladen sind. Ohne Daten bleiben die Accordions hidden.
   */
  updateTechnik() {
    const technik = this._section?.querySelector('[data-hali-bundle-technik]');
    const downloads = this._section?.querySelector('[data-hali-bundle-downloads]');
    const lieferung = this._section?.querySelector('[data-hali-bundle-lieferung]');
    if (!technik && !downloads && !lieferung) return;
    if (!this._produktDaten) return;

    const ordered = this._ordered ?? this._data?.components.get(String(this.dataset.variantId)) ?? [];

    if (technik) {
      technik.replaceChildren(...ordered.map((c) => this.renderTechnik(c)).filter(Boolean));
      this.toggleAccordion(technik);
    }
    if (downloads) {
      const seen = new Set();
      const sections = [];
      for (const component of ordered) {
        const handle = component.productVariant?.product?.handle;
        if (!handle || seen.has(handle)) continue;
        seen.add(handle);
        const section = this.renderDownloads(component, downloads);
        if (section) sections.push(section);
      }
      downloads.replaceChildren(...sections);
      this.toggleAccordion(downloads);
    }
    if (lieferung) {
      const seen = new Set();
      const sections = [];
      for (const component of ordered) {
        const handle = component.productVariant?.product?.handle;
        if (!handle || seen.has(handle)) continue;
        seen.add(handle);
        const section = this.renderLieferung(component);
        if (section) sections.push(section);
      }
      lieferung.replaceChildren(...sections);
      this.toggleAccordion(lieferung);
    }
  }

  /**
   * Lieferart der gewählten Komponenten-Variante (Text wie das Lieferinfo-Badge) mit
   * Vorschaubild der Variante wie in der Picker-Karte – zeigt also die gewählte Farbe.
   */
  renderLieferung(component) {
    const variant = component.productVariant ?? {};
    const product = variant.product ?? {};
    const daten = this._produktDaten.get(product.handle);
    const lieferart = daten?.variants?.[numericId(variant.id)]?.lieferart;
    const text = LIEFERART_TEXT[normalize(lieferart)];
    if (!text) return null;

    const section = document.createElement('div');
    section.className = 'pdp-accordion__component pdp-accordion__lieferung';

    const thumb = document.createElement('div');
    thumb.className = 'pdp-accordion__lieferung-thumb';
    this.setThumb(thumb, variant.image ?? product.featuredImage ?? null, product.title);

    const meta = document.createElement('div');
    meta.className = 'pdp-accordion__lieferung-meta';
    // Menge ist für die Lieferart irrelevant → Titel ohne „2 ×“
    const title = document.createElement('h3');
    title.className = 'pdp-accordion__component-title';
    title.textContent = product.title || variant.title || '';
    const textEl = document.createElement('p');
    textEl.className = 'pdp-accordion__lieferung-text';
    textEl.textContent = text;
    meta.append(title, textEl);

    section.append(thumb, meta);
    return section;
  }

  /** Accordion ohne eigene Inhalte (data-hali-autohide) nur zeigen, wenn Komponenten-Inhalte da sind. */
  toggleAccordion(container) {
    const details = container.closest('details.pdp-accordion');
    if (!details || !details.hasAttribute('data-hali-autohide')) return;
    details.hidden = container.childElementCount === 0;
  }

  /** Abschnitt mit Komponententitel; `null`, wenn es nichts zu zeigen gibt. */
  componentSection(component, body) {
    const variant = component.productVariant ?? {};
    const product = variant.product ?? {};
    const section = document.createElement('div');
    section.className = 'pdp-accordion__component';
    const title = document.createElement('h3');
    title.className = 'pdp-accordion__component-title';
    title.textContent = product.title || variant.title || '';
    this.setQuantity(title, component.quantity);
    section.append(title, body);
    return section;
  }

  /** Variantentext (ofmlvartext) + Maßzeichnung, Markup wie das Liquid-Accordion. */
  renderTechnik(component) {
    const variant = component.productVariant ?? {};
    const daten = this._produktDaten.get(variant.product?.handle);
    if (!daten) return null;
    const text = String(daten.variants?.[numericId(variant.id)]?.technik ?? '').trim();
    const bild = daten.technik_bild;
    if (!text && !bild) return null;

    const grid = document.createElement('div');
    grid.className = 'pdp-accordion__grid';
    if (!text || !bild) grid.classList.add('pdp-accordion__grid--text-only');

    if (text) {
      const col = document.createElement('div');
      col.className = 'pdp-accordion__text-col';
      const textEl = document.createElement('div');
      textEl.className = 'pdp-accordion__text rte';
      text.split(/\r?\n/).forEach((line, index) => {
        if (index > 0) textEl.appendChild(document.createElement('br'));
        textEl.appendChild(document.createTextNode(line));
      });
      col.appendChild(textEl);
      grid.appendChild(col);
    }

    if (bild?.src) {
      const wrap = document.createElement('div');
      wrap.className = 'pdp-accordion__image';
      if (!text) wrap.classList.add('pdp-accordion__image--capped');
      const img = document.createElement('img');
      img.src = bild.src;
      if (bild.srcset) img.srcset = bild.srcset;
      img.sizes = '(max-width: 749px) 100vw, 50vw';
      img.alt = `${variant.product?.title ?? ''} – Technische Daten`;
      img.loading = 'lazy';
      if (bild.width) img.width = bild.width;
      if (bild.height) img.height = bild.height;
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      wrap.appendChild(img);
      grid.appendChild(wrap);
    }

    return this.componentSection(component, grid);
  }

  /** Download-Liste eines Komponentenprodukts, Markup wie das Liquid-Accordion. */
  renderDownloads(component, container) {
    const daten = this._produktDaten.get(component.productVariant?.product?.handle);
    const files = (daten?.downloads ?? []).filter((file) => file?.url);
    if (!files.length) return null;

    const icon = container.parentElement?.querySelector('template[data-hali-download-icon]');
    const list = document.createElement('ul');
    list.className = 'pdp-accordion__downloads';
    for (const file of files) {
      const item = document.createElement('li');
      item.className = 'pdp-accordion__download-item';
      const link = document.createElement('a');
      link.className = 'pdp-accordion__download-link';
      link.href = file.url;
      link.target = '_blank';
      link.rel = 'noopener';
      const label = document.createElement('span');
      label.textContent = file.label || 'Download';
      link.appendChild(label);
      if (icon) link.appendChild(icon.content.cloneNode(true));
      item.appendChild(link);
      list.appendChild(item);
    }
    // Menge ist für Downloads irrelevant → Titel ohne „2 ×“
    return this.componentSection({ ...component, quantity: 1 }, list);
  }

  /* ── Swatches im Variantenpicker ──────────────────────────────────────── */

  decoratePicker() {
    const swatchOptions = this._data?.swatchOptions;
    const picker = this.picker;
    if (!swatchOptions?.length || !picker) return;

    for (const fieldset of picker.querySelectorAll('fieldset.variant-option--buttons')) {
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;

      const labels = [...fieldset.querySelectorAll('label.variant-option__button-label')];
      const inputs = labels.map((label) => label.querySelector('input[type="radio"]')).filter(Boolean);
      if (!inputs.length) continue;

      const optionLabel = this.legendText(legend);
      const productTitle =
        fieldset.closest('.hali-bundle-card')?.dataset.haliComponent ??
        legend.querySelector('.variant-option__bundle-product')?.textContent ??
        '';
      const match = this.findSwatchOption(swatchOptions, optionLabel, productTitle, inputs);
      if (!match) continue;

      this.decorateFieldset(fieldset, legend, labels, match.values);
    }
  }

  legendText(legend) {
    for (const node of legend.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return node.textContent.trim();
    }
    return '';
  }

  /**
   * Zuordnung Picker-Option → Komponentenoption:
   * 1. Komponententitel + Optionsname, 2. Optionsname allein,
   * 3. alle Optionswerte des Fieldsets haben in der Komponentenoption einen Swatch.
   */
  findSwatchOption(swatchOptions, optionLabel, productTitle, inputs) {
    const label = normalize(optionLabel);
    const title = normalize(productTitle);
    const values = inputs.map((input) => normalize(input.value));

    const coversAll = (option) => values.every((value) => option.values.has(value));
    const byName = swatchOptions.filter((option) => normalize(option.optionName) === label);

    return (
      byName.find((option) => title && normalize(option.productTitle) === title) ??
      byName.find(coversAll) ??
      byName[0] ??
      swatchOptions.find(coversAll) ??
      null
    );
  }

  decorateFieldset(fieldset, legend, labels, values) {
    let decorated = 0;

    for (const label of labels) {
      const input = label.querySelector('input[type="radio"]');
      const background = input ? values.get(normalize(input.value)) : null;
      if (!background) continue;

      if (!label.classList.contains('variant-option__button-label--has-swatch')) {
        label.classList.add('variant-option__button-label--has-swatch');
        label.querySelector('.variant-option__button-label__pill')?.remove();
        label.querySelector('.variant-option__button-label__text')?.remove();
      }

      let swatch = label.querySelector('.swatch');
      if (!swatch) {
        swatch = document.createElement('span');
        swatch.className = 'swatch swatch--unscaled';
        swatch.setAttribute('aria-hidden', 'true');
        input ? input.after(swatch) : label.prepend(swatch);
      }
      swatch.style.setProperty('--swatch-background', background);
      decorated += 1;
    }

    if (!decorated) return;

    for (const className of [...fieldset.classList]) {
      if (className.startsWith('variant-option--') && className !== 'variant-option--buttons') {
        fieldset.classList.remove(className);
      }
    }
    fieldset.classList.add('variant-option--swatches');
    fieldset.removeAttribute('data-option-id');

    const checked = labels.map((l) => l.querySelector('input:checked')).find(Boolean);
    let valueEl = legend.querySelector('.variant-option__swatch-value');
    if (!valueEl) {
      valueEl = document.createElement('span');
      valueEl.className = 'variant-option__swatch-value';
      const sublabel = legend.querySelector('.variant-option__bundle-product');
      sublabel ? sublabel.before(valueEl) : legend.appendChild(valueEl);
    }
    valueEl.textContent = checked?.value ?? '';
  }
}

if (!customElements.get('hali-bundle-inhalt')) {
  customElements.define('hali-bundle-inhalt', HaliBundleInhalt);
}
